/**
 * Docs Processing Hook (formerly Documents3)
 *
 * Headless hook for document upload and entity extraction.
 * Now uses artifact-based storage via /docs endpoint.
 *
 * Key features:
 * - Artifact-based storage: docs stored as Doc artifacts in PostgreSQL
 * - Graph-only architecture: entities stored directly in Fuseki
 * - Uses entityExtractionService via promptExecutor
 * - Returns structured extraction results (not Claude API format)
 * - Includes AI metadata (model, provider, tokens, duration)
 * - All steps logged to aiExecutionLogs
 */

import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  docsApi,
  type Document2ListItem,
  type Document2WithEntities,
  type Document3Entity,
  type Document3Relationship,
  type Document3ExtractionMetadata,
  type ContentType,
} from '@/lib/api/client'
import { getCurrentAccountId, getCurrentProjectId } from '@/lib/api/sparql-adapter'
import { useAIStore } from '@/stores/aiStore'
import { extractTextFromPdf, renderPdfToImages } from '@/lib/pdf/extractor'

// ============================================================================
// TYPES
// ============================================================================

export type ExtractionStage = 'idle' | 'analyzing' | 'storing' | 'complete' | 'error'

export interface ExtractionProgress {
  stage: ExtractionStage
  entitiesFound: number
  relationshipsFound: number
  metadata?: Document3ExtractionMetadata
  error?: string
}

export interface UseDocuments3ProcessingOptions {
  /** Auto-load documents list on mount */
  autoLoadDocuments?: boolean

  /** Callback when document is created */
  onDocumentCreated?: (doc: Document2WithEntities) => void

  /** Callback when entities are extracted and stored in graph */
  onEntitiesStored?: (result: {
    entities: Document3Entity[]
    metadata: Document3ExtractionMetadata
    entitiesCreated: number
    evidenceCreated: number
  }) => void

  /** Error handler */
  onError?: (error: Error) => void
}

export interface UseDocuments3ProcessingReturn {
  // ========== STATE ==========
  /** List of all documents */
  documents: Document2ListItem[]

  /** Currently selected document with entities */
  selectedDoc: Document2WithEntities | null

  /** Loading states */
  isLoadingDocuments: boolean
  isLoadingDocument: boolean

  /** Extraction progress with metadata */
  extractionProgress: ExtractionProgress

  /** AI configuration status */
  isConfigured: boolean

  /** General error */
  error: Error | null

  // ========== ACTIONS ==========
  /** Upload a file and create document */
  uploadDocument: (
    file: File,
    contentType: ContentType,
    title?: string
  ) => Promise<Document2WithEntities>

  /** Paste content and create document */
  pasteContent: (
    title: string,
    content: string,
    contentType: ContentType
  ) => Promise<Document2WithEntities>

  /** Extract entities and store directly in graph (combined flow) */
  extractAndStore: (docId: string) => Promise<{
    entities: Document3Entity[]
    relationships: Document3Relationship[]
    metadata: Document3ExtractionMetadata
    entitiesCreated: number
    evidenceCreated: number
  }>

  /** Select a document (loads full details) */
  selectDocument: (docId: string) => Promise<void>

  /** Refresh documents list */
  refreshDocuments: () => Promise<void>

  /** Clear error */
  clearError: () => void

  /** Reset extraction progress */
  resetProgress: () => void
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

export function useDocuments3Processing(
  options: UseDocuments3ProcessingOptions = {}
): UseDocuments3ProcessingReturn {
  const {
    autoLoadDocuments = true,
    onDocumentCreated,
    onEntitiesStored,
    onError,
  } = options

  const { apiKey, isConfigured } = useAIStore()
  const queryClient = useQueryClient()

  // Local state
  const [extractionProgress, setExtractionProgress] = useState<ExtractionProgress>({
    stage: 'idle',
    entitiesFound: 0,
    relationshipsFound: 0,
  })
  const [error, setError] = useState<Error | null>(null)
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null)

  // Get account/project context
  const accountId = getCurrentAccountId()
  const projectId = getCurrentProjectId()
  const hasContext = !!accountId && !!projectId

  // ========== QUERIES ==========

  // Fetch documents list
  const {
    data: documents = [],
    isLoading: isLoadingDocuments,
    error: documentsError,
  } = useQuery({
    queryKey: ['docs', 'list', accountId, projectId],
    queryFn: async () => {
      if (!accountId || !projectId) {
        throw new Error('Missing account or project context')
      }
      return docsApi.list(accountId, projectId)
    },
    enabled: hasContext && autoLoadDocuments,
    staleTime: 2 * 60 * 1000, // 2 minutes
  })

  // Fetch selected document
  const { data: selectedDoc, isLoading: isLoadingSelectedDoc } = useQuery({
    queryKey: ['docs', 'document', selectedDocId],
    queryFn: async () => {
      if (!accountId || !projectId || !selectedDocId) {
        return null
      }
      return docsApi.get(accountId, projectId, selectedDocId)
    },
    enabled: !!accountId && !!projectId && !!selectedDocId,
    staleTime: 1 * 60 * 1000, // 1 minute
  })

  // ========== MUTATIONS ==========

  // Create document mutation
  const createDocumentMutation = useMutation({
    mutationFn: async (data: {
      title: string
      contentType: ContentType
      rawContent: string
      markdownContent?: string
      fileName?: string
      fileType?: 'pdf' | 'md' | 'txt'
      fileData?: string // base64 encoded file for PDF preview
    }) => {
      if (!accountId || !projectId) {
        throw new Error('Missing account or project context')
      }
      return docsApi.create(accountId, projectId, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['docs', 'list'] })
    },
    onError: (err) => {
      setError(err as Error)
      onError?.(err as Error)
    },
  })

  // Extract entities mutation
  const extractEntitiesMutation = useMutation({
    mutationFn: async (data: {
      docId: string
      content: string
      contentType: ContentType
      fileData?: string
      fileName?: string
    }) => {
      if (!accountId || !projectId || !apiKey) {
        throw new Error('Missing context or API key')
      }

      setExtractionProgress({
        stage: 'analyzing',
        entitiesFound: 0,
        relationshipsFound: 0,
      })

      // Check if we should use visual analysis
      // Auto-enable if: PDF with insufficient text (<500 chars) and fileData exists
      const isPdf = data.fileName?.toLowerCase().endsWith('.pdf') ?? false
      const hasInsufficientText = data.content.length < 500
      const shouldUseVisual = isPdf && hasInsufficientText && !!data.fileData

      let images: Array<{
        pageNumber: number
        dataUrl: string
        base64Data: string
        mediaType: string
      }> | undefined

      // If visual analysis needed, render PDF to images
      if (shouldUseVisual && data.fileData) {
        try {
          // Convert base64 to File object
          const byteString = atob(data.fileData)
          const bytes = new Uint8Array(byteString.length)
          for (let i = 0; i < byteString.length; i++) {
            bytes[i] = byteString.charCodeAt(i)
          }
          const file = new File([bytes], data.fileName || 'document.pdf', { type: 'application/pdf' })

          // Render PDF to images
          const pageImages = await renderPdfToImages(file.arrayBuffer(), {
            scale: 1.0, // Lower scale for faster processing and smaller payload
            maxPages: 20, // Limit to first 20 pages
          })

          // Convert to API format
          images = pageImages.map((img) => {
            // Extract base64 data and mediaType from dataUrl
            const match = img.dataUrl.match(/^data:([^;]+);base64,(.+)$/)
            const mediaType = match?.[1] || 'image/png'
            const base64Data = match?.[2] || img.dataUrl

            return {
              pageNumber: img.pageNumber,
              dataUrl: img.dataUrl,
              base64Data,
              mediaType,
            }
          })
        } catch (error) {
          console.warn('Failed to render PDF images, falling back to text', error)
        }
      }

      // Call docs analyze endpoint (returns structured data)
      const result = await docsApi.analyze(accountId, projectId, {
        apiKey,
        content: data.content,
        contentType: data.contentType,
        fileData: data.fileData,
        fileName: data.fileName,
        enableVisualAnalysis: shouldUseVisual,
        images,
      })

      return result
    },
    onSuccess: (result) => {
      if (!result.success) {
        setExtractionProgress({
          stage: 'error',
          entitiesFound: 0,
          relationshipsFound: 0,
          metadata: result.metadata,
          error: result.error || 'Entity extraction failed',
        })
        return
      }

      setExtractionProgress({
        stage: 'storing',
        entitiesFound: result.entities.length,
        relationshipsFound: result.relationships.length,
        metadata: result.metadata,
      })
    },
    onError: (err) => {
      setExtractionProgress((prev) => ({
        ...prev,
        stage: 'error',
        error: (err as Error).message,
      }))
      setError(err as Error)
      onError?.(err as Error)
    },
  })

  // Store entities in graph mutation (graph-only architecture)
  const storeMutation = useMutation({
    mutationFn: async (data: {
      docId: string
      entities: Document3Entity[]
      relationships?: Document3Relationship[]
    }) => {
      if (!accountId || !projectId) {
        throw new Error('Missing account or project context')
      }
      return docsApi.storeEntities(accountId, projectId, data.docId, data)
    },
    onSuccess: (_result, variables) => {
      setExtractionProgress((prev) => ({
        ...prev,
        stage: 'complete',
      }))

      queryClient.invalidateQueries({ queryKey: ['docs', 'document', variables.docId] })
      queryClient.invalidateQueries({ queryKey: ['docs', 'list'] })
    },
    onError: (err) => {
      setExtractionProgress((prev) => ({
        ...prev,
        stage: 'error',
        error: (err as Error).message,
      }))
      setError(err as Error)
      onError?.(err as Error)
    },
  })

  // ========== ACTIONS ==========

  const uploadDocument = useCallback(
    async (
      file: File,
      contentType: ContentType,
      title?: string
    ): Promise<Document2WithEntities> => {
      if (!isConfigured) {
        throw new Error('Claude API key not configured')
      }

      // Determine file type
      const isPdf = file.name.toLowerCase().endsWith('.pdf')
      const fileType = isPdf ? 'pdf' : file.name.endsWith('.md') ? 'md' : 'txt'

      // Read file content with proper handling for PDFs
      let content: string
      let fileData: string | undefined
      if (isPdf) {
        const extracted = await extractTextFromPdf(file)
        content = extracted.fullText
        // Also store the original PDF as base64 for preview
        const arrayBuffer = await file.arrayBuffer()
        fileData = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)))
      } else {
        content = await file.text()
      }

      // Create document
      const doc = await createDocumentMutation.mutateAsync({
        title: title || file.name,
        contentType,
        rawContent: content,
        markdownContent: content,
        fileName: file.name,
        fileType,
        fileData,
      })

      // Load full document with entities
      const fullDoc = await docsApi.get(accountId!, projectId!, doc.id)

      onDocumentCreated?.(fullDoc)
      return fullDoc
    },
    [isConfigured, accountId, projectId, createDocumentMutation, onDocumentCreated]
  )

  const pasteContent = useCallback(
    async (
      title: string,
      content: string,
      contentType: ContentType
    ): Promise<Document2WithEntities> => {
      if (!isConfigured) {
        throw new Error('Claude API key not configured')
      }

      // Create document
      const doc = await createDocumentMutation.mutateAsync({
        title,
        contentType,
        rawContent: content,
        markdownContent: content,
      })

      // Load full document with entities
      const fullDoc = await docsApi.get(accountId!, projectId!, doc.id)

      onDocumentCreated?.(fullDoc)
      return fullDoc
    },
    [isConfigured, accountId, projectId, createDocumentMutation, onDocumentCreated]
  )

  const extractAndStore = useCallback(
    async (
      docId: string
    ): Promise<{
      entities: Document3Entity[]
      relationships: Document3Relationship[]
      metadata: Document3ExtractionMetadata
      entitiesCreated: number
      evidenceCreated: number
    }> => {
      if (!isConfigured) {
        throw new Error('Claude API key not configured')
      }

      // Get document
      const doc = await docsApi.get(accountId!, projectId!, docId)

      // Get file data if document is a PDF (for visual analysis fallback)
      let fileData: string | undefined
      const isPdf = doc.document.fileName?.toLowerCase().endsWith('.pdf') ?? false

      if (isPdf) {
        try {
          const fileResult = await docsApi.getFile(accountId!, projectId!, docId)
          fileData = fileResult.fileData
        } catch (error) {
          // File data not available, continue with text-only
          console.warn('Failed to fetch file data for visual analysis fallback', error)
        }
      }

      // Extract entities via AI (with automatic visual analysis fallback)
      const extractResult = await extractEntitiesMutation.mutateAsync({
        docId,
        content: doc.document.rawContent,
        contentType: doc.document.contentType,
        fileData,
        fileName: doc.document.fileName || undefined,
      })

      if (!extractResult.success) {
        throw new Error(extractResult.error || 'Entity extraction failed')
      }

      // Store entities directly in graph (graph-only architecture)
      const storeResult = await storeMutation.mutateAsync({
        docId,
        entities: extractResult.entities,
        relationships: extractResult.relationships,
      })

      onEntitiesStored?.({
        entities: extractResult.entities,
        metadata: extractResult.metadata,
        entitiesCreated: storeResult.entitiesCreated,
        evidenceCreated: storeResult.evidenceCreated,
      })

      return {
        entities: extractResult.entities,
        relationships: extractResult.relationships,
        metadata: extractResult.metadata,
        entitiesCreated: storeResult.entitiesCreated,
        evidenceCreated: storeResult.evidenceCreated,
      }
    },
    [
      isConfigured,
      accountId,
      projectId,
      extractEntitiesMutation,
      storeMutation,
      onEntitiesStored,
    ]
  )

  const selectDocument = useCallback(async (docId: string): Promise<void> => {
    setSelectedDocId(docId)
  }, [])

  const refreshDocuments = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['docs', 'list'] })
  }, [queryClient])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const resetProgress = useCallback(() => {
    setExtractionProgress({
      stage: 'idle',
      entitiesFound: 0,
      relationshipsFound: 0,
    })
  }, [])

  return {
    // State
    documents,
    selectedDoc: selectedDoc || null,
    isLoadingDocuments,
    isLoadingDocument:
      isLoadingSelectedDoc ||
      extractEntitiesMutation.isPending ||
      storeMutation.isPending,
    extractionProgress,
    isConfigured,
    error: error || (documentsError as Error | null),

    // Actions
    uploadDocument,
    pasteContent,
    extractAndStore,
    selectDocument,
    refreshDocuments,
    clearError,
    resetProgress,
  }
}
